'use client';

import * as React from 'react';
import {
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  type TooltipContentProps,
} from 'recharts';

import { cn } from '@v2/utils';

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode;
    color?: string;
  }
>;

type ChartContextValue = {
  config: ChartConfig;
};

const ChartContext = React.createContext<ChartContextValue | null>(null);

function useChart() {
  const context = React.useContext(ChartContext);

  if (!context) {
    throw new Error('useChart must be used within a ChartContainer');
  }

  return context;
}

const ChartContainer = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & {
    config: ChartConfig;
    children: React.ComponentProps<typeof ResponsiveContainer>['children'];
  }
>(({ id, className, children, config, ...props }, ref) => {
  const generatedId = React.useId();
  const chartId = `chart-${id || generatedId.replace(/:/g, '')}`;

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        ref={ref}
        data-chart={chartId}
        className={cn(
          'flex aspect-video justify-center text-xs [&_.recharts-layer]:outline-none [&_.recharts-surface]:outline-none',
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <ResponsiveContainer>{children}</ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  );
});

ChartContainer.displayName = 'ChartContainer';

function ChartStyle({ id, config }: { id: string; config: ChartConfig }) {
  const colorConfig = Object.entries(config).filter(([, value]) => value.color);

  if (!colorConfig.length) {
    return null;
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `
[data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => `  --color-${key}: ${itemConfig.color};`)
  .join('\n')}
}
`,
      }}
    />
  );
}

const ChartTooltip = RechartsTooltip;

function ChartTooltipContent({
  active,
  label,
  payload = [],
}: Partial<TooltipContentProps<number, string>>) {
  const { config } = useChart();

  if (!active || !payload.length) {
    return null;
  }

  const value = payload[0]?.value;
  const seriesKey = payload[0]?.dataKey;
  const seriesConfig =
    typeof seriesKey === 'string' ? config[seriesKey] : undefined;

  return (
    <div className="min-w-[8rem] rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-md">
      <p className="font-medium text-gray-800">{label}</p>
      <div className="mt-1 flex items-center justify-between gap-4 text-gray-600">
        <span>{seriesConfig?.label ?? 'Tickets'}</span>
        <span className="font-semibold tabular-nums text-gray-900">
          {value}
        </span>
      </div>
    </div>
  );
}

export { ChartContainer, ChartTooltip, ChartTooltipContent };
